import EdgeScroll from '../components/EdgeScroll';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Title, Paper, Text, Group, Stack, Button, Table, Badge, TextInput,
  Textarea, Drawer, Loader, Flex, SimpleGrid, Select, Switch, Tooltip,
  ActionIcon, Tabs, ThemeIcon, Modal, Menu, SegmentedControl,
} from '@mantine/core';
import {
  Buildings, Plus, Trash, Pencil, UserCircle, Megaphone,
  CurrencyDollar, CaretRight, ArrowLeft, FloppyDisk,
  ToggleLeft, ToggleRight, Globe, Phone, Envelope, Note, ArrowsClockwise,
  DotsThree, FileText, FilePdf, FileXls, CalendarBlank, Image, UserSwitch,
} from '@phosphor-icons/react';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import SEO from '../components/SEO';
import AnimatedLogo from '../components/AnimatedLogo';
import ExportMenu from '../components/ExportMenu';
import api from '../utils/api';
import { formatMoney, currencySymbol } from '../utils/money';
import { CampaignDrawer } from './Campaigns';

// Dominująca (wg wydatków) waluta zbioru kampanii/klientów. Koszty są RAW w walucie
// konta. Agencje z kontami w RÓŻNYCH walutach: sumy zbiorcze są przybliżone (patrz
// znane ograniczenia w planie), dane per kampania/klient są zawsze we własnej walucie.
function dominantCurrency(list, key = 'currency') {
  const spend = {};
  (list || []).forEach((c) => {
    const cur = c[key] || 'PLN';
    spend[cur] = (spend[cur] || 0) + parseFloat(c.cost || c.total_spend || 0);
  });
  const keys = Object.keys(spend);
  if (!keys.length) return 'PLN';
  return keys.reduce((a, b) => (spend[a] >= spend[b] ? a : b));
}

function PlanCard({ plan }) {
  const { t } = useTranslation();
  if (!plan) return null;
  const usagePercent = plan.max_campaigns > 0 ? (plan.managed_count / plan.max_campaigns) * 100 : 0;
  const included = plan.included_campaigns || 10;
  const extraCount = Math.max(0, plan.managed_count - included);
  const extraPrice = plan.extra_price || 149;
  const basePrice = plan.base_price || 1999;

  let costBreakdown;
  if (plan.custom_price) {
    costBreakdown = t('agency.planCard.customPrice');
  } else if (plan.managed_count <= included) {
    costBreakdown = t('agency.planCard.costIncluded', { price: basePrice.toLocaleString('pl-PL'), included });
  } else {
    costBreakdown = t('agency.planCard.costExtra', { price: basePrice.toLocaleString('pl-PL'), extra: extraCount, extraPrice });
  }

  return (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
      <Paper withBorder p="sm" radius="md">
        <Text size="xs" c="dimmed">{t('agency.planCard.managedCampaigns')}</Text>
        <Group gap={4} align="baseline">
          <Text size="lg" fw={700} ff="monospace">{plan.managed_count}</Text>
          <Text size="xs" c="dimmed">/ {plan.max_campaigns}</Text>
        </Group>
        <Box mt={6} h={4} bg="gray.2" style={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box h={4} bg={usagePercent > 90 ? 'red' : 'brand'} style={{ width: `${Math.min(usagePercent, 100)}%`, borderRadius: 2 }} />
        </Box>
      </Paper>
      <Paper withBorder p="sm" radius="md">
        <Text size="xs" c="dimmed">{t('agency.planCard.costNet')}</Text>
        <Group gap={4} align="baseline">
          <Text size="lg" fw={700} ff="monospace">{plan.monthly_cost.toLocaleString('pl-PL')} zł</Text>
          <Text size="xs" c="dimmed">{t('agency.planCard.vat')}</Text>
        </Group>
        <Text size="xs" c="dimmed">{costBreakdown}</Text>
      </Paper>
      <Paper withBorder p="sm" radius="md">
        <Text size="xs" c="dimmed">{t('agency.planCard.allCampaigns')}</Text>
        <Text size="lg" fw={700} ff="monospace">{plan.total_campaigns}</Text>
      </Paper>
      <Paper withBorder p="sm" radius="md">
        <Text size="xs" c="dimmed">{t('agency.planCard.totalGoogleSpend')}</Text>
        <Text size="lg" fw={700} ff="monospace">{formatMoney(plan.total_spend, plan.currency || 'PLN')}</Text>
      </Paper>
    </SimpleGrid>
  );
}

function ClientForm({ initial, onSave, onCancel, saving }) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [gadsId, setGadsId] = useState(initial?.google_ads_customer_id || '');
  const [budget, setBudget] = useState(initial?.monthly_budget || '');
  const [notes, setNotes] = useState(initial?.notes || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name,
      email,
      phone,
      google_ads_customer_id: gadsId,
      monthly_budget: budget || null,
      notes,
    });
  };

  const labelStyle = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="sm">
        <TextInput label={t('agency.form.clientName')} value={name} onChange={e => setName(e.target.value)} required size="sm" styles={{ label: labelStyle }} placeholder={t('agency.form.clientNamePlaceholder')} />
        <Group grow>
          <TextInput label={t('agency.form.email')} value={email} onChange={e => setEmail(e.target.value)} size="sm" styles={{ label: labelStyle }} leftSection={<Envelope size={14} />} />
          <TextInput label={t('agency.form.phone')} value={phone} onChange={e => setPhone(e.target.value)} size="sm" styles={{ label: labelStyle }} leftSection={<Phone size={14} />} />
        </Group>
        <Group grow>
          <TextInput label={t('agency.form.googleAdsId')} value={gadsId} onChange={e => setGadsId(e.target.value)} size="sm" styles={{ label: labelStyle }} placeholder="123-456-7890" leftSection={<Globe size={14} />} />
          <TextInput label={t('agency.form.monthlyBudget', { cur: currencySymbol(initial?.currency) })} value={budget} onChange={e => setBudget(e.target.value)} size="sm" styles={{ label: labelStyle }} type="number" leftSection={<CurrencyDollar size={14} />} />
        </Group>
        <Textarea label={t('agency.form.notes')} value={notes} onChange={e => setNotes(e.target.value)} size="sm" styles={{ label: labelStyle }} rows={2} />
        <Group>
          <Button type="submit" color="brand" size="sm" loading={saving}>{initial ? t('agency.form.save') : t('agency.form.addClient')}</Button>
          {onCancel && <Button variant="subtle" color="gray" size="sm" onClick={onCancel}>{t('agency.form.cancel')}</Button>}
        </Group>
      </Stack>
    </form>
  );
}

export default function AgencyMode() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedClient, setSelectedClientRaw] = useState(null);
  // Selecting a client deep-links to ?client=<id> so it has its own URL.
  const setSelectedClient = useCallback((c) => {
    setSelectedClientRaw(c);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (c) params.set('client', c.id); else params.delete('client');
      return params;
    }, { replace: true });
  }, [setSearchParams]);
  // Open the full campaign detail drawer in place (same component as the
  // campaigns list), without leaving the agency view.
  const [drawerCampaign, setDrawerCampaign] = useState(null);
  const openCampaign = useCallback((c) => {
    setDrawerCampaign(c);
    // Deep-link the open campaign so the URL can be copied and reopened.
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('campaign', c.id);
      return params;
    });
  }, [setSearchParams]);
  const closeCampaignDrawer = useCallback(() => {
    setDrawerCampaign(null);
    // The drawer manipulates the URL (?campaign&tab); restore the agency URL.
    setSearchParams(selectedClient ? { client: selectedClient.id } : {}, { replace: true });
  }, [selectedClient, setSearchParams]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [syncingClientId, setSyncingClientId] = useState(null);

  const org = user?.organization;

  const loadData = useCallback(async () => {
    try {
      const [ovRes, clRes, cpRes] = await Promise.all([
        api.get('/ads/agency/overview/'),
        api.get('/ads/agency/clients/'),
        api.get('/ads/agency/campaigns/'),
      ]);
      setOverview(ovRes.data);
      setClients(clRes.data);
      setCampaigns(cpRes.data);
    } catch {
      // silently handle
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Open a client from the ?client=<id> URL param once clients are loaded.
  useEffect(() => {
    const cid = searchParams.get('client');
    if (cid && clients.length && (!selectedClient || selectedClient.id !== cid)) {
      const c = clients.find(cl => String(cl.id) === cid);
      if (c) setSelectedClientRaw(c);
    }
  }, [clients, searchParams]); // eslint-disable-line

  // Open (or close) the campaign drawer from the ?campaign=<id> URL param, so a
  // copied agency URL reopens the same campaign when navigated to.
  useEffect(() => {
    const cpid = searchParams.get('campaign');
    if (cpid && campaigns.length) {
      if (!drawerCampaign || String(drawerCampaign.id) !== cpid) {
        const cp = campaigns.find(c => String(c.id) === cpid);
        if (cp) setDrawerCampaign(cp);
      }
    } else if (!cpid && drawerCampaign) {
      setDrawerCampaign(null);
    }
  }, [campaigns, searchParams]); // eslint-disable-line

  const handleCreateClient = async (data) => {
    setSaving(true);
    try {
      await api.post('/ads/agency/clients/', data);
      setShowAddClient(false);
      loadData();
    } catch (err) {
      addNotification(err.response?.data?.detail || t('agency.notifications.createError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateClient = async (data) => {
    setSaving(true);
    try {
      await api.patch(`/ads/agency/clients/${editingClient.id}/`, data);
      setEditingClient(null);
      loadData();
    } catch {
      addNotification(t('agency.notifications.updateError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    setDeletingId(clientId);
    try {
      await api.delete(`/ads/agency/clients/${clientId}/`);
      if (selectedClient?.id === clientId) setSelectedClient(null);
      loadData();
    } catch {
      addNotification(t('agency.notifications.deleteError'), 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleDroid = async (campaign, action) => {
    setTogglingId(campaign.id);
    try {
      const res = await api.post(`/ads/agency/campaigns/${campaign.id}/toggle/`, { action });
      addNotification(res.data.message, 'success');
      loadData();
    } catch (err) {
      addNotification(err.response?.data?.error || t('agency.notifications.toggleError'), 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const handleAssignCampaign = async (campaignId, clientId) => {
    try {
      await api.post('/ads/agency/campaigns/assign/', {
        campaign_id: campaignId,
        client_id: clientId || null,
      });
      loadData();
    } catch {
      addNotification(t('agency.notifications.assignError'), 'error');
    }
  };

  const handleSaveNotes = async (campaignId) => {
    try {
      await api.patch(`/ads/agency/campaigns/${campaignId}/notes/`, { notes: notesText });
      setEditingNotes(null);
      loadData();
    } catch {
      addNotification(t('agency.notifications.notesError'), 'error');
    }
  };

  const handleSyncClient = async (clientId) => {
    setSyncingClientId(clientId);
    const notifId = addNotification(t('agency.notifications.syncing'), 'loading');
    try {
      const res = await api.post(`/ads/agency/clients/${clientId}/sync/`);
      addNotification(res.data.message, 'success');
      loadData();
    } catch (err) {
      addNotification(err.response?.data?.error || t('agency.notifications.syncError'), 'error');
    } finally {
      setSyncingClientId(null);
    }
  };

  if (!org?.is_agency) {
    return (
      <Box px="lg" py="md">
        <Title order={3} mb="sm">{t('agency.title')}</Title>
        <Paper withBorder p="xl" radius="md" ta="center">
          <Buildings size={32} color="var(--mantine-color-gray-4)" />
          <Text size="sm" fw={500} mt="sm">{t('agency.notActive.title')}</Text>
          <Text size="xs" c="dimmed">{t('agency.notActive.text')}</Text>
        </Paper>
      </Box>
    );
  }

  if (loading) return (
    <Flex align="center" justify="center" h={300}>
      <AnimatedLogo size={48} />
    </Flex>
  );

  const clientOptions = [
    { value: '', label: t('agency.unassigned') },
    ...clients.map(c => ({ value: c.id, label: c.name })),
  ];

  // Client detail view
  if (selectedClient) {
    const clientCampaigns = campaigns.filter(c => c.agency_client === selectedClient.id);
    return (
      <Box px="lg" py="md">
        <Group mb="md">
          <Button variant="subtle" color="gray" size="sm" leftSection={<ArrowLeft size={14} />} onClick={() => setSelectedClient(null)}>
            {t('agency.clientDetail.allClients')}
          </Button>
        </Group>

        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <ThemeIcon variant="light" color="brand" size="lg" radius="md"><UserCircle size={20} /></ThemeIcon>
            <Box>
              <Title order={3}>{selectedClient.name}</Title>
              <Group gap="xs">
                {selectedClient.email && <Text size="xs" c="dimmed">{selectedClient.email}</Text>}
                {selectedClient.google_ads_customer_id && <Badge size="xs" variant="light" color="blue">{selectedClient.google_ads_customer_id}</Badge>}
              </Group>
            </Box>
          </Group>
          <Group gap="xs">
            {clientCampaigns.length > 0 && (
              <ExportMenu
                baseUrl={`/ads/agency/clients/${selectedClient.id}/campaigns/export/`}
                filename={`kampanie-${(selectedClient.name || 'klient').replace(/[^a-zA-Z0-9ąęśćźżółńĄĘŚĆŹŻÓŁŃ ]/g, '').replace(/\s+/g, '-').toLowerCase()}`}
              />
            )}
            {selectedClient.google_ads_customer_id && (
              <Button
                variant="light"
                color="blue"
                size="sm"
                leftSection={<ArrowsClockwise size={14} />}
                loading={syncingClientId === selectedClient.id}
                onClick={() => handleSyncClient(selectedClient.id)}
              >
                {t('agency.clientDetail.sync')}
              </Button>
            )}
            <Button variant="light" size="sm" leftSection={<Pencil size={14} />} onClick={() => setEditingClient(selectedClient)}>{t('agency.clientDetail.edit')}</Button>
          </Group>
        </Group>

        {selectedClient.notes && (
          <Paper withBorder p="sm" radius="md" mb="md" bg="gray.0">
            <Text size="xs" c="dimmed" mb={2}>{t('agency.clientDetail.notesLabel')}</Text>
            <Text size="sm">{selectedClient.notes}</Text>
          </Paper>
        )}

        <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm" mb="md">
          <Paper withBorder p="sm" radius="md">
            <Text size="xs" c="dimmed">{t('agency.clientDetail.campaigns')}</Text>
            <Text size="lg" fw={700} ff="monospace">{clientCampaigns.length}</Text>
          </Paper>
          <Paper withBorder p="sm" radius="md">
            <Text size="xs" c="dimmed">{t('agency.clientDetail.managed')}</Text>
            <Text size="lg" fw={700} ff="monospace">{clientCampaigns.filter(c => c.is_managed).length}</Text>
          </Paper>
          <Paper withBorder p="sm" radius="md">
            <Text size="xs" c="dimmed">{t('agency.clientDetail.googleSpend')}</Text>
            <Text size="lg" fw={700} ff="monospace">{formatMoney(clientCampaigns.reduce((s, c) => s + parseFloat(c.cost || 0), 0), dominantCurrency(clientCampaigns))}</Text>
          </Paper>
          {selectedClient.monthly_budget && (
            <Paper withBorder p="sm" radius="md">
              <Text size="xs" c="dimmed">{t('agency.clientDetail.monthlyBudget')}</Text>
              <Text size="lg" fw={700} ff="monospace">{formatMoney(selectedClient.monthly_budget, selectedClient.currency || dominantCurrency(clientCampaigns))}</Text>
            </Paper>
          )}
        </SimpleGrid>

        <CampaignTable
          campaigns={clientCampaigns}
          emptyVariant="client"
          onOpenCampaign={openCampaign}
          clients={clients}
          togglingId={togglingId}
          editingNotes={editingNotes}
          notesText={notesText}
          onToggle={handleToggleDroid}
          onAssign={handleAssignCampaign}
          onEditNotes={(c) => { setEditingNotes(c.id); setNotesText(c.notes || ''); }}
          onSaveNotes={handleSaveNotes}
          onCancelNotes={() => setEditingNotes(null)}
          setNotesText={setNotesText}
          clientOptions={clientOptions}
          plan={overview?.plan}
          addNotification={addNotification}
          org={org}
        />

        <Drawer opened={!!editingClient} onClose={() => setEditingClient(null)} title={<Text size="sm" fw={600}>{t('agency.clientDetail.editClient')}</Text>} position="right" size="md" padding="md">
          <ClientForm initial={editingClient} onSave={handleUpdateClient} onCancel={() => setEditingClient(null)} saving={saving} />
        </Drawer>
        {drawerCampaign && (
          <CampaignDrawer campaign={drawerCampaign} onClose={closeCampaignDrawer} onCampaignUpdated={loadData} />
        )}
      </Box>
    );
  }

  // Main agency view
  return (
    <Box px="lg" py="md">
      <SEO title={t('agency.title')} path="/app/agency" noIndex />
      <Group justify="space-between" mb="md">
        <Title order={3}>{t('agency.title')}</Title>
        <Button leftSection={<Plus size={14} />} color="brand" onClick={() => setShowAddClient(true)}>
          {t('agency.createClient')}
        </Button>
      </Group>

      <PlanCard plan={overview?.plan} />

      <Tabs
        value={searchParams.get('tab') || 'clients'}
        onChange={(v) => {
          const params = new URLSearchParams(searchParams);
          params.set('tab', v);
          setSearchParams(params, { replace: true });
        }}
        mt="lg"
      >
        <Tabs.List>
          <Tabs.Tab value="clients">{t('agency.tabs.clients', { count: clients.length })}</Tabs.Tab>
          <Tabs.Tab value="campaigns">{t('agency.tabs.campaigns', { count: campaigns.length })}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="clients" pt="md">

          <Drawer opened={showAddClient} onClose={() => setShowAddClient(false)} title={<Text size="sm" fw={600}>{t('agency.newClient')}</Text>} position="right" size="md" padding="md">
            <ClientForm onSave={handleCreateClient} onCancel={() => setShowAddClient(false)} saving={saving} />
          </Drawer>

          {clients.length > 0 ? (
            <>
            {/* Mobile client cards */}
            <div className="md:hidden">
              <Stack gap="xs">
                {clients.map(client => (
                  <Paper key={client.id} withBorder p="sm" radius="md" onClick={() => setSelectedClient(client)} style={{ cursor: 'pointer' }}>
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={600} truncate>{client.name}</Text>
                        {client.notes && <Note size={12} color="var(--mantine-color-gray-5)" />}
                      </Group>
                      <Group gap={4} onClick={e => e.stopPropagation()}>
                        {client.google_ads_customer_id && <ActionIcon variant="subtle" color="blue" size="sm" loading={syncingClientId === client.id} onClick={() => handleSyncClient(client.id)}><ArrowsClockwise size={14} /></ActionIcon>}
                        <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => setEditingClient(client)}><Pencil size={14} /></ActionIcon>
                        <ActionIcon variant="subtle" color="red" size="sm" loading={deletingId === client.id} onClick={() => handleDeleteClient(client.id)}><Trash size={14} /></ActionIcon>
                      </Group>
                    </Group>
                    {client.email && <Text size="xs" c="dimmed" mb={4}>{client.email}</Text>}
                    {client.google_ads_customer_id && <Text size="xs" ff="monospace" c="dimmed" mb={6}>{client.google_ads_customer_id}</Text>}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
                      <div><Text size="xs" c="dimmed">{t('agency.clientsTable.campaigns')}</Text><Text size="sm" ff="monospace" fw={500}>{client.campaigns_count}</Text></div>
                      <div><Text size="xs" c="dimmed">{t('agency.clientsTable.managed')}</Text><Text size="sm" ff="monospace" fw={500} c={client.managed_campaigns_count > 0 ? 'green.6' : 'dimmed'}>{client.managed_campaigns_count}</Text></div>
                      <div><Text size="xs" c="dimmed">{t('agency.clientsTable.spend')}</Text><Text size="sm" ff="monospace" fw={500}>{formatMoney(client.total_spend, client.currency || 'PLN')}</Text></div>
                    </div>
                  </Paper>
                ))}
              </Stack>
            </div>
            {/* Desktop client table */}
            <Paper withBorder radius="md" style={{ overflow: 'hidden' }} className="hidden md:block">
              <EdgeScroll>
              <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" style={{ minWidth: 600 }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('agency.clientsTable.client')}</Table.Th>
                    <Table.Th>{t('agency.clientsTable.googleAdsId')}</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{t('agency.clientsTable.campaigns')}</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{t('agency.clientsTable.managed')}</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{t('agency.clientsTable.spend')}</Table.Th>
                    <Table.Th w={80}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {clients.map(client => (
                    <Table.Tr key={client.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedClient(client)}>
                      <Table.Td>
                        <Group gap="xs">
                          <Text size="sm" fw={500}>{client.name}</Text>
                          {client.notes && <Note size={12} color="var(--mantine-color-gray-5)" />}
                        </Group>
                        {client.email && <Text size="xs" c="dimmed">{client.email}</Text>}
                      </Table.Td>
                      <Table.Td>
                        {client.google_ads_customer_id ? (
                          <Text size="xs" ff="monospace" c="dimmed">{client.google_ads_customer_id}</Text>
                        ) : <Text size="xs" c="gray.4">-</Text>}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" ff="monospace">{client.campaigns_count}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" ff="monospace" c={client.managed_campaigns_count > 0 ? 'green.6' : 'dimmed'}>
                          {client.managed_campaigns_count}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" ff="monospace">{formatMoney(client.total_spend, client.currency || 'PLN')}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} justify="flex-end" onClick={e => e.stopPropagation()}>
                          {client.google_ads_customer_id && (
                            <Tooltip label={t('agency.clientsTable.syncTooltip')}><ActionIcon variant="subtle" color="blue" size="sm" loading={syncingClientId === client.id} onClick={() => handleSyncClient(client.id)}><ArrowsClockwise size={14} /></ActionIcon></Tooltip>
                          )}
                          <Tooltip label={t('agency.clientsTable.editTooltip')}><ActionIcon variant="subtle" color="gray" size="sm" onClick={() => setEditingClient(client)}><Pencil size={14} /></ActionIcon></Tooltip>
                          <Tooltip label={t('agency.clientsTable.deleteTooltip')}><ActionIcon variant="subtle" color="red" size="sm" loading={deletingId === client.id} onClick={() => handleDeleteClient(client.id)}><Trash size={14} /></ActionIcon></Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </EdgeScroll>
            </Paper>
            </>
          ) : !showAddClient && (
            <Paper withBorder p="xl" radius="md" ta="center">
              <UserCircle size={32} color="var(--mantine-color-gray-4)" />
              <Text size="sm" fw={500} mt="sm">{t('agency.clientsTable.emptyTitle')}</Text>
              <Text size="xs" c="dimmed">{t('agency.clientsTable.emptyText')}</Text>
            </Paper>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="campaigns" pt="md">
          <CampaignTable
            campaigns={campaigns}
            emptyVariant="all"
            onOpenCampaign={openCampaign}
            clients={clients}
            togglingId={togglingId}
            editingNotes={editingNotes}
            notesText={notesText}
            onToggle={handleToggleDroid}
            onAssign={handleAssignCampaign}
            onEditNotes={(c) => { setEditingNotes(c.id); setNotesText(c.notes || ''); }}
            onSaveNotes={handleSaveNotes}
            onCancelNotes={() => setEditingNotes(null)}
            setNotesText={setNotesText}
            clientOptions={clientOptions}
            plan={overview?.plan}
            addNotification={addNotification}
            org={org}
          />
        </Tabs.Panel>
      </Tabs>

      <Drawer opened={!!editingClient} onClose={() => setEditingClient(null)} title={<Text size="sm" fw={600}>{t('agency.clientDetail.editClient')}</Text>} position="right" size="md" padding="md">
        <ClientForm initial={editingClient} onSave={handleUpdateClient} onCancel={() => setEditingClient(null)} saving={saving} />
      </Drawer>
      {drawerCampaign && (
        <CampaignDrawer campaign={drawerCampaign} onClose={closeCampaignDrawer} onCampaignUpdated={loadData} />
      )}
    </Box>
  );
}

function ReportModal({ campaign, onClose, addNotification, org }) {
  const { t } = useTranslation();
  const PERIOD_OPTIONS = t('agency.report.periodOptions', { returnObjects: true });
  const [period, setPeriod] = useState('last_30');
  const [format, setFormat] = useState('pdf');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const params = new URLSearchParams({
        campaign_id: campaign.id,
        period,
        // Use export_format, not `format`: `format` is a reserved DRF query
        // param (content negotiation) and triggers a 404 before the view runs.
        export_format: format,
      });
      if (period === 'custom') {
        if (!customFrom || !customTo) {
          addNotification(t('agency.report.dateError'), 'error');
          setGenerating(false);
          return;
        }
        params.set('date_from', customFrom);
        params.set('date_to', customTo);
      }

      const res = await api.get(`/accounts/report/campaign/?${params.toString()}`, {
        responseType: 'blob',
      });

      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const fileName = `raport-${campaign.name.replace(/[^a-zA-Z0-9ąęśćźżółńĄĘŚĆŹŻÓŁŃ ]/g, '').replace(/\s+/g, '-').toLowerCase()}.${ext}`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      addNotification(t('agency.report.downloaded', { name: campaign.name }), 'success');
      onClose();
    } catch {
      addNotification(t('agency.report.error'), 'error');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title={<Text size="sm" fw={600}>{t('agency.report.title')}</Text>} centered size="md">
      <Stack gap="md">
        <Paper bg="gray.0" p="sm" radius="md" withBorder>
          <Text size="xs" c="dimmed">{t('agency.report.campaign')}</Text>
          <Text size="sm" fw={600}>{campaign.name}</Text>
          {campaign.client_name && <Text size="xs" c="dimmed">{t('agency.clientLabel', { name: campaign.client_name })}</Text>}
        </Paper>

        <Select
          label={t('agency.report.period')}
          data={PERIOD_OPTIONS}
          value={period}
          onChange={setPeriod}
          size="sm"
          leftSection={<CalendarBlank size={14} />}
          styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
        />

        {period === 'custom' && (
          <Group grow>
            <TextInput
              label={t('agency.report.from')}
              type="date"
              value={customFrom}
              onChange={e => setCustomFrom(e.target.value)}
              size="sm"
              styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
            />
            <TextInput
              label={t('agency.report.to')}
              type="date"
              value={customTo}
              onChange={e => setCustomTo(e.target.value)}
              size="sm"
              styles={{ label: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' } }}
            />
          </Group>
        )}

        <Box>
          <Text size="xs" fw={600} mb={6} style={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-6)' }}>
            {t('agency.report.format')}
          </Text>
          <SegmentedControl
            value={format}
            onChange={setFormat}
            fullWidth
            data={[
              { value: 'pdf', label: (
                <Group gap={6} justify="center">
                  <FilePdf size={16} weight="duotone" />
                  <span>PDF</span>
                </Group>
              )},
              { value: 'xlsx', label: (
                <Group gap={6} justify="center">
                  <FileXls size={16} weight="duotone" />
                  <span>Excel</span>
                </Group>
              )},
            ]}
            size="sm"
          />
        </Box>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('agency.report.cancel')}</Button>
          <Button
            color="brand"
            onClick={handleGenerate}
            loading={generating}
            leftSection={generating ? null : <FileText size={16} />}
          >
            {generating ? t('agency.report.generating') : t('agency.report.download')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

const PLATFORM_LABEL = { google_ads: 'Google Ads', meta_ads: 'Meta Ads', microsoft_ads: 'Microsoft Ads', tiktok_ads: 'TikTok Ads' };
const PLATFORM_COLOR = { google_ads: 'blue', meta_ads: 'indigo', microsoft_ads: 'cyan', tiktok_ads: 'dark' };
const TYPE_LABEL = { search: 'Search', performance_max: 'Performance Max', display: 'Display', shopping: 'Shopping', video: 'Video' };

function CampaignMeta({ c }) {
  const platform = PLATFORM_LABEL[c.platform] || 'Google Ads';
  const type = TYPE_LABEL[c.campaign_type] || (c.campaign_type ? c.campaign_type : 'Search');
  return (
    <Group gap={4} mt={2}>
      <Badge size="xs" variant="light" color={PLATFORM_COLOR[c.platform] || 'blue'}>{platform}</Badge>
      <Badge size="xs" variant="light" color="gray">{type}</Badge>
    </Group>
  );
}

function CampaignTable({ campaigns, clients, togglingId, editingNotes, notesText, onToggle, onAssign, onEditNotes, onSaveNotes, onCancelNotes, setNotesText, clientOptions, plan, addNotification, org, emptyVariant = 'all', onOpenCampaign }) {
  const { t } = useTranslation();
  const [reportCampaign, setReportCampaign] = useState(null);
  // Reassign-client modal: holds the campaign being reassigned + the chosen client.
  const [assignCampaign, setAssignCampaign] = useState(null);
  const [assignValue, setAssignValue] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const openAssign = (c) => { setAssignCampaign(c); setAssignValue(c.agency_client || ''); };
  const handleAssignSave = async () => {
    if (!assignCampaign) return;
    setAssignSaving(true);
    try {
      await onAssign(assignCampaign.id, assignValue);
      setAssignCampaign(null);
    } finally {
      setAssignSaving(false);
    }
  };

  if (campaigns.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md" ta="center">
        <Megaphone size={32} color="var(--mantine-color-gray-4)" />
        <Text size="sm" fw={500} mt="sm">{t('agency.campaignTable.emptyTitle')}</Text>
        <Text size="xs" c="dimmed">
          {emptyVariant === 'client'
            ? t('agency.campaignTable.emptyClient')
            : t('agency.campaignTable.emptyAll')}
        </Text>
      </Paper>
    );
  }

  const atLimit = plan && plan.managed_count >= plan.max_campaigns;

  return (
    <>
    {/* Mobile campaign cards */}
    <div className="md:hidden">
      <Stack gap="xs">
        {campaigns.map(c => {
          const managed = c.is_managed;
          return (
            <Paper key={c.id} withBorder p="sm" radius="md" style={{ borderLeft: managed ? '3px solid var(--mantine-color-brand-5)' : undefined }}>
              <Group justify="space-between" mb={4}>
                <Text size="sm" fw={600} style={{ flex: 1, minWidth: 0, cursor: onOpenCampaign ? 'pointer' : undefined }} truncate onClick={() => onOpenCampaign && onOpenCampaign(c)}>{c.name}</Text>
                <Badge size="xs" variant="light" color={c.status === 'active' ? 'green' : c.status === 'paused' ? 'yellow' : 'gray'}>
                  {c.status === 'active' ? t('agency.campaignTable.statusActive') : c.status === 'paused' ? t('agency.campaignTable.statusPaused') : c.status}
                </Badge>
              </Group>
              <CampaignMeta c={c} />
              {c.client_name ? <Text size="xs" c="dimmed" mt={4} mb={4}>{t('agency.clientLabel', { name: c.client_name })}</Text> : null}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 8 }}>
                <div><Text size="xs" c="dimmed">{t('agency.campaignTable.spend')}</Text><Text size="sm" ff="monospace" fw={500}>{formatMoney(c.cost, c.currency)}</Text></div>
                <div><Text size="xs" c="dimmed">{t('agency.campaignTable.clicks')}</Text><Text size="sm" ff="monospace">{c.clicks?.toLocaleString('pl-PL') || 0}</Text></div>
              </div>
              <Group justify="space-between">
                <Group gap="xs">
                  <Tooltip label={c.agency_client ? t('agency.campaignTable.changeClient') : t('agency.campaignTable.assignClient')}>
                    <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => openAssign(c)}><UserSwitch size={14} /></ActionIcon>
                  </Tooltip>
                  <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => setReportCampaign(c)}><FileText size={14} /></ActionIcon>
                  <ActionIcon variant={c.notes ? 'light' : 'subtle'} color={c.notes ? 'brand' : 'gray'} size="sm" onClick={() => onEditNotes(c)}><Note size={14} /></ActionIcon>
                </Group>
                <Switch
                  checked={managed}
                  onChange={() => onToggle(c, managed ? 'disable' : 'enable')}
                  disabled={togglingId === c.id || (!managed && atLimit)}
                  color="brand"
                  size="md"
                  styles={{ track: { cursor: togglingId === c.id || (!managed && atLimit) ? 'not-allowed' : 'pointer' } }}
                />
              </Group>
              {editingNotes === c.id && (
                <Paper bg="gray.0" p="sm" radius="sm" withBorder mt="xs">
                  <Textarea value={notesText} onChange={e => setNotesText(e.target.value)} size="sm" rows={2} placeholder={t('agency.campaignTable.notePlaceholder')} mb="xs" />
                  <Group gap="xs">
                    <Button size="compact-xs" color="brand" leftSection={<FloppyDisk size={12} />} onClick={() => onSaveNotes(c.id)}>{t('agency.campaignTable.save')}</Button>
                    <Button size="compact-xs" variant="subtle" color="gray" onClick={onCancelNotes}>{t('agency.campaignTable.cancel')}</Button>
                  </Group>
                </Paper>
              )}
            </Paper>
          );
        })}
      </Stack>
    </div>
    {/* Desktop campaign table */}
    <Paper withBorder radius="md" style={{ overflow: 'hidden' }} className="hidden md:block">
      <EdgeScroll>
      <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" style={{ minWidth: 860 }}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('agency.campaignTable.campaign')}</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>{t('agency.campaignTable.status')}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{t('agency.campaignTable.budget')}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{t('agency.campaignTable.spend')}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{t('agency.campaignTable.clicks')}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{t('agency.campaignTable.ctr')}</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>{t('agency.campaignTable.conversions')}</Table.Th>
            <Table.Th style={{ textAlign: 'center' }}>Droid</Table.Th>
            <Table.Th w={60}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {campaigns.map(c => {
            const managed = c.is_managed;
            return (
              <React.Fragment key={c.id}>
                <Table.Tr>
                  <Table.Td>
                    <Text size="sm" fw={600} c="brand.7" style={{ cursor: onOpenCampaign ? 'pointer' : undefined }} onClick={() => onOpenCampaign && onOpenCampaign(c)}>{c.name}</Text>
                    <CampaignMeta c={c} />
                    {c.client_name && <Text size="xs" c="dimmed" mt={2}>{t('agency.clientLabel', { name: c.client_name })}</Text>}
                    {c.google_campaign_id && <Text size="xs" ff="monospace" c="dimmed" mt={2}>{c.google_campaign_id}</Text>}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'center' }}>
                    <Badge size="xs" variant="light" color={c.status === 'active' ? 'green' : c.status === 'paused' ? 'yellow' : 'gray'}>
                      {c.status === 'active' ? t('agency.campaignTable.statusActive') : c.status === 'paused' ? t('agency.campaignTable.statusPaused') : c.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="sm" ff="monospace">{formatMoney(c.budget_amount, c.currency)}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="sm" ff="monospace">{formatMoney(c.cost, c.currency)}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="sm" ff="monospace">{c.clicks?.toLocaleString('pl-PL') || 0}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="sm" ff="monospace">{parseFloat(c.ctr || 0).toFixed(1)}%</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="sm" ff="monospace">{parseFloat(c.conversions || 0).toFixed(0)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Flex justify="center">
                      <Tooltip label={managed ? t('agency.campaignTable.disableTooltip') : atLimit && !managed ? t('agency.campaignTable.limitTooltip', { max: plan.max_campaigns }) : t('agency.campaignTable.enableTooltip')}>
                        <span style={{ display: 'inline-flex' }}>
                          <Switch
                            checked={managed}
                            onChange={() => onToggle(c, managed ? 'disable' : 'enable')}
                            disabled={togglingId === c.id || (!managed && atLimit)}
                            color="brand"
                            size="sm"
                            styles={{ track: { cursor: togglingId === c.id || (!managed && atLimit) ? 'not-allowed' : 'pointer' } }}
                          />
                        </span>
                      </Tooltip>
                    </Flex>
                  </Table.Td>
                  <Table.Td>
                    <Menu shadow="md" width={180} position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" size="sm">
                          <DotsThree size={18} weight="bold" />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {onOpenCampaign && (
                          <Menu.Item leftSection={<CaretRight size={14} />} onClick={() => onOpenCampaign(c)}>
                            {t('agency.campaignTable.campaignDetails')}
                          </Menu.Item>
                        )}
                        <Menu.Item leftSection={<FileText size={14} />} onClick={() => setReportCampaign(c)}>
                          {t('agency.campaignTable.generateReport')}
                        </Menu.Item>
                        <Menu.Item leftSection={<UserSwitch size={14} />} onClick={() => openAssign(c)}>
                          {c.agency_client ? t('agency.campaignTable.changeClient') : t('agency.campaignTable.assignClient')}
                        </Menu.Item>
                        <Menu.Item leftSection={<Note size={14} />} onClick={() => onEditNotes(c)}>
                          {c.notes ? t('agency.campaignTable.editNote') : t('agency.campaignTable.addNote')}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
                {editingNotes === c.id && (
                  <Table.Tr>
                    <Table.Td colSpan={9} style={{ padding: '0 var(--mantine-spacing-sm) var(--mantine-spacing-sm)' }}>
                      <Paper bg="gray.0" p="sm" radius="sm" withBorder mt={4}>
                        <Textarea value={notesText} onChange={e => setNotesText(e.target.value)} size="sm" rows={2} placeholder={t('agency.campaignTable.campaignNotePlaceholder')} mb="xs" />
                        <Group gap="xs">
                          <Button size="compact-xs" color="brand" leftSection={<FloppyDisk size={12} />} onClick={() => onSaveNotes(c.id)}>{t('agency.campaignTable.save')}</Button>
                          <Button size="compact-xs" variant="subtle" color="gray" onClick={onCancelNotes}>{t('agency.campaignTable.cancel')}</Button>
                        </Group>
                      </Paper>
                    </Table.Td>
                  </Table.Tr>
                )}
              </React.Fragment>
            );
          })}
        </Table.Tbody>
      </Table>
      </EdgeScroll>
    </Paper>

    {reportCampaign && (
      <ReportModal
        campaign={reportCampaign}
        onClose={() => setReportCampaign(null)}
        addNotification={addNotification}
        org={org}
      />
    )}

    <Modal
      opened={!!assignCampaign}
      onClose={() => setAssignCampaign(null)}
      title={<Text size="sm" fw={600}>{t('agency.campaignTable.assignModalTitle')}</Text>}
      centered
      size="sm"
    >
      {assignCampaign && (
        <Stack gap="md">
          <Paper bg="gray.0" p="sm" radius="md" withBorder>
            <Text size="xs" c="dimmed">{t('agency.campaignTable.campaign')}</Text>
            <Text size="sm" fw={600}>{assignCampaign.name}</Text>
          </Paper>
          <Select
            label={t('agency.campaignTable.clientSelectLabel')}
            value={assignValue}
            onChange={(v) => setAssignValue(v || '')}
            data={clientOptions}
            placeholder={t('agency.campaignTable.clientSelectPlaceholder')}
            searchable
            comboboxProps={{ withinPortal: true }}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" color="gray" size="sm" onClick={() => setAssignCampaign(null)}>{t('agency.campaignTable.cancel')}</Button>
            <Button color="brand" size="sm" leftSection={<FloppyDisk size={14} />} loading={assignSaving} onClick={handleAssignSave}>{t('agency.campaignTable.save')}</Button>
          </Group>
        </Stack>
      )}
    </Modal>
    </>
  );
}
